/**
 * @fileoverview Provider-specific copy and status text for the Sending
 * domain manager (`SendingDomainsSection.tsx`). Split out because component
 * files export only components (`react-refresh/only-export-components`).
 *
 * `inkbox` is the original wording, unchanged. `ses` (MOR-2322, G6) differs
 * where the vendors do: SES verifies on its own schedule with no billing
 * line, a removal is immediate with nothing to restore, and rotating an Easy
 * DKIM key needs no new DNS record.
 */

export type SendingDomainsProvider = "inkbox" | "ses";

export interface StatusMeta {
  label: string;
  dot: string;
  text: string;
  explain: string;
}

/** What SES says differently from Inkbox for a status it shares, or adds (`temporary_failure`). */
export const SES_STATUS_OVERRIDES: Record<string, Partial<StatusMeta>> = {
  pending: {
    label: "Waiting for DNS",
    explain:
      "Publish the DNS records below. SES checks them on its own and verifies the domain, usually within minutes and up to 72 hours.",
  },
  verified: {
    explain:
      "Healthy. Mailboxes can be created on this domain, and what they send is DKIM-signed.",
  },
  failed: {
    explain:
      "SES could not find the records. Compare each record below with your registrar, correct it, then check verification again.",
  },
  temporary_failure: {
    label: "Retrying",
    dot: "bg-amber-500",
    text: "text-amber-700 dark:text-amber-400",
    explain:
      "SES could not check the records just now and will try again. Check verification again in a few minutes.",
  },
};

/** Row labels for the records SES asks for; Inkbox rows keep their own `purpose` text. */
export const SES_PURPOSE_LABELS: Record<string, string> = {
  dkim: "DKIM",
  mail_from: "MAIL FROM",
  spf: "SPF",
  inbound: "Inbound mail",
  dmarc_recommended: "DMARC (recommended)",
};

export const SES_RECORDS_NOTE =
  "The DKIM, MAIL FROM and SPF records verify sending. The inbound MX routes replies to Morgan, and the DMARC record is a recommendation SES does not check.";

export interface SendingDomainsCopy {
  description: string;
  addHint: string;
  footer: string;
  removeDescription: string;
  removedToast: string;
  rotateDescription: string;
  rotatedToast: string;
  apexConfirmLabel: string;
  apexDescription: (domain: string) => string;
}

export const SENDING_DOMAINS_COPY: Record<
  SendingDomainsProvider,
  SendingDomainsCopy
> = {
  inkbox: {
    description:
      "Send from your own domain instead of the platform default. Add a domain, publish its DNS records at your registrar, and it verifies automatically.",
    addHint:
      "Use a subdomain (recommended) so your existing email keeps working — e.g. mail.yourfirm.com.au. Inkbox bills US$4/month per domain from the moment it verifies.",
    footer:
      "The default domain applies to newly created mailboxes across your organisation — existing mailboxes keep their current address. Adding, removing, and switching domains requires an organisation admin.",
    removeDescription:
      "Sending and receiving on this domain stop immediately. You can restore it within 24 hours; after that the removal is permanent. Any mailboxes still using the domain must be removed first.",
    removedToast: "is being removed — you can restore it for 24 hours",
    rotateDescription:
      "A new signing key is issued and you'll need to publish an updated TXT record at your registrar. Sending continues on the current key until the new record verifies, so there's no interruption.",
    rotatedToast:
      "New DKIM key issued — publish the updated TXT record at your registrar",
    apexConfirmLabel: "Replace existing provider",
    apexDescription: (domain) =>
      `${domain} has existing MX records, which means a mail provider is already handling its email. Continuing will point the domain's mail at Inkbox and replace that provider. In most cases you should add a subdomain (like mail.${domain}) instead.`,
  },
  ses: {
    description:
      "Send from your own domain instead of the deployment's mail domain. Add a domain, publish its DNS records at your registrar, and SES verifies it automatically.",
    addHint:
      "Use a subdomain (recommended) so your existing email keeps working — e.g. mail.yourfirm.com.au. You'll get DNS records to publish; there is no per-domain charge.",
    footer:
      "The default domain applies to newly created mailboxes across your organisation — existing mailboxes keep their current address. Adding, removing, and switching domains requires an organisation admin. Receiving mail on your own domain also needs the inbound MX record published and the domain enabled by Morgan: contact support once the records are in place.",
    removeDescription:
      "The domain is removed from the mail system at once and can't be restored; add it again to get fresh DNS records. Any mailboxes still using the domain must be removed first.",
    removedToast: "was removed",
    rotateDescription:
      "SES issues a new 2048-bit DKIM signing key. Your DKIM records point at keys SES hosts, so there is nothing to republish and sending isn't interrupted. SES allows this once a day.",
    rotatedToast: "DKIM signing key refreshed — no DNS change needed",
    apexConfirmLabel: "Use this domain anyway",
    apexDescription: (domain) =>
      `${domain} already has MX records, so a mail provider is handling its email. Publishing the inbound MX record you'll be given would replace it. In most cases you should add a subdomain (like mail.${domain}) instead.`,
  },
};
